/**
 * Pure calculation engine for the Wealth Projection Planner.
 * No UI, no side effects — accepts a validated input object, returns
 * a deterministic annual dataset + summary. All UI (charts, tables,
 * KPI cards, insights) reads from this same output so the app is
 * always internally consistent.
 */

export const MAX_DURATION_YEARS = 100;

/**
 * Validate + normalise a raw input object. Returns `{ input, notice }`
 * where `notice` is a friendly inline message the UI can display
 * (or null if inputs were clean).
 */
export function normalizeInput(raw) {
  const clamp = (v, min, max) => Math.min(Math.max(Number(v) || 0, min), max);
  let lumpSum = Math.max(0, Number(raw.lumpSum) || 0);
  let monthlySip = Math.max(0, Number(raw.monthlySip) || 0);
  let stepUpPct = clamp(raw.stepUpPct, 0, 100);
  let annualReturnPct = clamp(raw.annualReturnPct, 0, 100);
  let durationYears = clamp(raw.durationYears, 1, MAX_DURATION_YEARS);
  let sipStopYear = clamp(raw.sipStopYear, 0, durationYears);
  let yearsToWithdrawal = clamp(raw.yearsToWithdrawal, 0, MAX_DURATION_YEARS);

  const notices = [];

  // Withdrawal cannot exceed duration — auto-cap.
  if (sipStopYear + yearsToWithdrawal > durationYears) {
    const originalWait = yearsToWithdrawal;
    yearsToWithdrawal = Math.max(0, durationYears - sipStopYear);
    notices.push(
      `Years to withdrawal capped at ${yearsToWithdrawal} to keep the plan within ${durationYears} years ` +
      `(you requested ${originalWait}).`
    );
  }

  return {
    input: { lumpSum, monthlySip, stepUpPct, annualReturnPct, durationYears, sipStopYear, yearsToWithdrawal },
    notice: notices.length ? notices.join(' ') : null,
  };
}

/**
 * Run the projection.
 *   - Lump sum invested at the start of Year 1.
 *   - SIP contribution at end of every month.
 *   - Monthly compounding rate = (1 + annualRate)^(1/12) - 1.
 *   - SIP steps up once at the start of every contribution year.
 *   - After sipStopYear, SIP = 0 and portfolio compounds only.
 *   - Simulation ends at min(sipStopYear + yearsToWithdrawal, durationYears).
 *
 * Returns:
 *   annual: [{ year, phase, startingBalance, monthlySip, stepUpPct,
 *              annualContribution, cumulativeSip, lumpSum, totalInvested,
 *              growthDuringYear, endingBalance, annualIncrease }]
 *   summary: { finalValue, totalInvested, lumpSum, totalSip, totalGrowth,
 *              wealthMultiple, finalSip, cagr, valueAtSipStop, valueAtWithdrawal,
 *              growthAfterSipStop, pctIncreaseAfterSipStop, gainSharePct,
 *              contribSharePct, effectiveEndYear }
 */
export function computeProjection(rawInput) {
  const { input, notice } = normalizeInput(rawInput);
  const { lumpSum, monthlySip, stepUpPct, annualReturnPct, durationYears, sipStopYear, yearsToWithdrawal } = input;

  const monthlyRate = Math.pow(1 + annualReturnPct / 100, 1 / 12) - 1;
  const stepUp = 1 + stepUpPct / 100;

  const effectiveEndYear = Math.min(sipStopYear + yearsToWithdrawal, durationYears);
  const totalYears = Math.max(1, effectiveEndYear);

  const annual = [];
  let balance = lumpSum;
  let currentMonthlySip = monthlySip;
  let cumulativeSip = 0;
  let valueAtSipStop = null;
  let finalSip = 0;

  for (let year = 1; year <= totalYears; year += 1) {
    const startingBalance = balance;
    const contributing = year <= sipStopYear;

    // Apply step-up at the START of every contribution year (year >= 2).
    if (contributing && year > 1) {
      currentMonthlySip = currentMonthlySip * stepUp;
    }

    const monthlySipForYear = contributing ? currentMonthlySip : 0;
    let annualContribution = 0;

    // 12 months of compounding + end-of-month SIP.
    for (let m = 0; m < 12; m += 1) {
      balance = balance * (1 + monthlyRate);
      if (contributing) {
        balance += monthlySipForYear;
        annualContribution += monthlySipForYear;
      }
    }

    cumulativeSip += annualContribution;
    if (contributing) finalSip = monthlySipForYear;
    const endingBalance = balance;
    const growthDuringYear = endingBalance - startingBalance - annualContribution;

    let phase;
    if (year === effectiveEndYear && year > sipStopYear) phase = 'Planned Withdrawal';
    else if (contributing) phase = 'Accumulation';
    else phase = 'Compounding';

    annual.push({
      year,
      phase,
      startingBalance,
      monthlySip: monthlySipForYear,
      stepUpPct: contributing && year > 1 ? stepUpPct : 0,
      annualContribution,
      cumulativeSip,
      lumpSum,
      totalInvested: lumpSum + cumulativeSip,
      growthDuringYear,
      endingBalance,
      annualIncrease: endingBalance - startingBalance,
    });

    if (year === sipStopYear) valueAtSipStop = endingBalance;
  }

  // If sipStopYear === 0 (no SIP at all), valueAtSipStop is just lumpSum.
  if (valueAtSipStop === null) valueAtSipStop = sipStopYear === 0 ? lumpSum : annual[annual.length - 1].endingBalance;

  const finalRow = annual[annual.length - 1];
  const finalValue = finalRow.endingBalance;
  const totalSip = cumulativeSip;
  const totalInvested = lumpSum + totalSip;
  const totalGrowth = finalValue - totalInvested;
  const wealthMultiple = totalInvested > 0 ? finalValue / totalInvested : 0;

  const cagr = totalYears > 0 && totalInvested > 0
    ? Math.pow(Math.max(finalValue, 1e-9) / Math.max(totalInvested, 1), 1 / totalYears) - 1
    : 0;

  const growthAfterSipStop = finalValue - valueAtSipStop;
  const pctIncreaseAfterSipStop = valueAtSipStop > 0 ? (growthAfterSipStop / valueAtSipStop) * 100 : 0;
  const gainSharePct = finalValue > 0 ? (totalGrowth / finalValue) * 100 : 0;
  const contribSharePct = finalValue > 0 ? (totalInvested / finalValue) * 100 : 0;

  return {
    input,
    notice,
    annual,
    summary: {
      finalValue,
      totalInvested,
      lumpSum,
      totalSip,
      totalGrowth,
      wealthMultiple,
      finalSip,
      annualReturnPct,
      cagr: cagr * 100,
      valueAtSipStop,
      valueAtWithdrawal: finalValue,
      growthAfterSipStop,
      pctIncreaseAfterSipStop,
      gainSharePct,
      contribSharePct,
      effectiveEndYear,
      contributionYears: Math.min(sipStopYear, totalYears),
      compoundingOnlyYears: Math.max(0, totalYears - sipStopYear),
    },
  };
}

// ---- Formatters used by every UI surface ---------------------------------

const nfExact = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/**
 * Human-friendly currency formatting for KPI cards and charts.
 * Uses abbreviations at $1K and above, exact for smaller values.
 */
export function fmtCurrencyShort(v) {
  if (!isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(abs / 1e12 >= 100 ? 0 : 2)}T`;
  if (abs >= 1e9)  return `${sign}$${(abs / 1e9).toFixed(abs / 1e9 >= 100 ? 0 : 2)}B`;
  if (abs >= 1e6)  return `${sign}$${(abs / 1e6).toFixed(abs / 1e6 >= 100 ? 0 : 2)}M`;
  if (abs >= 1e3)  return `${sign}$${(abs / 1e3).toFixed(abs / 1e3 >= 100 ? 0 : 1)}K`;
  return nfExact.format(v);
}

export function fmtCurrencyExact(v) {
  if (!isFinite(v)) return '—';
  return nfExact.format(Math.round(v));
}

export function fmtPct(v, digits = 1) {
  if (!isFinite(v)) return '—';
  return `${v.toFixed(digits)}%`;
}

export function fmtMultiple(v) {
  if (!isFinite(v) || v === 0) return '—';
  return `${v.toFixed(2)}×`;
}
